import type { CommunityFeedSurveyDto, SurveyDetailsV2Dto } from "@/api/types";
import { CountdownBadge } from "@/components/Badge/CountdownBadge";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { AutoTranslatedLabel } from "@/components/Label/AutoTranslatedLabel";
import { MultiLineContent } from "@/components/MultiLineContent/MultiLineContent";
import { ShareButton } from "@/components/ShareButton/ShareButton";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { getCommunitySurveyCtaLabel } from "@/helpers/community-feed";
import { isContentAutoTranslated } from "@/helpers/languages";
import { getIsSurveySharable } from "@/helpers/surveys";
import { useProject } from "@/hooks/Network/useProject";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { surveyMutations } from "@/queries/surveys/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { CommunityPost } from "./CommunityPost";
import { useTrackCommunityPostViewCount } from "./helpers";

interface CommunitySurveyProps {
  survey: CommunityFeedSurveyDto;
}

export function CommunitySurvey({ survey }: CommunitySurveyProps): React.ReactNode {
  const [isAppSurveyModalOpen, appSurveyModalHandler] = useBool(false);

  const project = useProject();
  const sessionUser = useSessionUser();
  const { t } = useTranslation();
  const { onStartTracking, onCancelTracking } = useTrackCommunityPostViewCount({
    itemId: survey.id,
    type: "survey",
    isEnabled: !survey.viewTracked,
  });

  const updateSurveyReadStatusMutation = surveyMutations.useMarkSurveyAsRead(survey.id);

  const menuActions: ContextMenuItem[] = [
    {
      labels: {
        default: t("component.community-post.actions.mark-as-read"),
      },
      "data-testid": "post-context-menu-mark-read",
      value: "markAsRead",
      icon: <CheckIcon />,
      type: "button",
      callbackFn: updateSurveyReadStatusMutation.mutate,
      isHidden: !survey.isUnread,
    },
  ];

  const handleEnterViewport = (): void => {
    onStartTracking();
  };

  const handleLeaveViewport = (): void => {
    if (!updateSurveyReadStatusMutation.isPending && survey.isUnread) {
      updateSurveyReadStatusMutation.mutate();
    }

    onCancelTracking();
  };

  const endDate = survey.endsAt ? new Date(survey.endsAt) : undefined;
  const isAutomatedSurvey = survey.scope === "automated";
  const isAutoTranslated = isContentAutoTranslated(survey.originalLanguageId, sessionUser);
  const canBeShared = getIsSurveySharable(survey);

  return (
    <>
      <CommunityPost.Root
        isUnread={survey.isUnread}
        user={survey.createdBy}
        group={survey.group}
        actions={menuActions}
        dateSubtitle={survey.startsAt && <FormattedDate format="datetime" date={survey.startsAt} />}
        onEnterViewport={handleEnterViewport}
        onLeaveViewport={handleLeaveViewport}
      >
        <Stack gap="md">
          {/* Countdown label */}
          {endDate && <CountdownBadge endDate={endDate} />}

          {/* Content */}
          <p className="text-body-emphasis whitespace-pre-line">{survey.title}</p>
          <span className="text-body">
            <MultiLineContent shouldHighlightLinks>{survey.description.trim()}</MultiLineContent>
          </span>

          {/* CTA */}
          <Button
            variant="filled"
            type="link"
            href={routes.surveys.respond({ slug: project.slug, id: survey.id })}
            state={{
              category: (isAutomatedSurvey ? "feelAtHome" : "other") satisfies SurveyDetailsV2Dto["category"],
            }}
            data-testid="community-survey-cta-btn"
            onClick={() => updateSurveyReadStatusMutation.mutate()}
            disabled={survey.canRespond !== "yes"}
          >
            {getCommunitySurveyCtaLabel(t, survey)}
          </Button>

          {(isAutoTranslated || canBeShared) && (
            <Inline gap="lg" align="center" justify={isAutoTranslated ? "between" : "end"}>
              {isAutoTranslated && <AutoTranslatedLabel />}
              {canBeShared && <ShareButton entityId={survey.id} entityType="survey" variant="ghostPrimary" />}
            </Inline>
          )}
        </Stack>
      </CommunityPost.Root>

      <ConfirmModal
        title={t("component.community-post.app-survey.modal.title")}
        description={t("component.community-post.app-survey.modal.description")}
        renderDescription={(description) => (
          <Stack align="center">
            <span>{description}</span>
            <Button type="link" variant="ghostPrimary" href={t("component.community-post.app-survey.modal.link")}>
              {t("component.community-post.app-survey.modal.link")}
            </Button>
          </Stack>
        )}
        isLoading={false}
        tone="primary"
        onResolve={appSurveyModalHandler.setFalse}
        onReject={appSurveyModalHandler.setFalse}
        rejectBtnProps={{
          hide: true,
        }}
        resolveBtnProps={{
          "data-testid": "app-survey-modal-confirm",
          text: t("common.action.ok"),
        }}
        isOpened={isAppSurveyModalOpen}
        shouldCloseOnEsc
        data-testid="cancel-app-survey-modal"
        onOpenChange={appSurveyModalHandler.set}
      />
    </>
  );
}
